import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute, Params} from '@angular/router';
import {CardFormComponent, IOptionsCardForm} from 'libraries/module/formbuilder/forms/card-form/card-form.component';
import {ALL_ARRAY_FIELDS, ALL_DATE_FIELDS, ALL_FORM_ARRAY_FIELDS, ALL_TEXT_FIELDS, formGroupConfig} from './config';
import {BookingSearchParameters} from 'app_sasha/shared/models/BookingSearchParameters';
import {CARD_OPTION_LABELS} from 'app_sasha/shared/config/wording';
import {IFieldSpecs} from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import {FIELD_DESCRIPTION_LABELS, TEMPLATE_LABELS} from 'app_sasha/shared/config/booking-wording';
import {ChipsComponent} from 'libraries/module/formbuilder/fields/chips/chips.component';
import {ConfigFieldBuilderService} from './config-field-builder.service';
import {
    IDescriptionLabels
} from 'libraries/module/formbuilder/fields/form-field/components/field-description/field-description.component';
import {SearchUtils} from 'app_sasha/shared/models/SearchUtils';
import {
    BookingSearchParametersShortName,
    IBookingSearchParametersURL
} from 'libraries/interfaces/IBookingSearchParameters';
import {SearchUtilsService} from "../../models/search-utils.service";
import { ESashaPermissions } from "libraries/enums/e-sasha-permissions";

interface IFieldConfig {
    [name: string]: IFieldSpecs;
}

const FIELDSET_STATES: { [key: string]: boolean } = {
    bookingFieldset: true,
    journeyFieldset: false,
    travellerFieldset: false,
    flightFieldset: false,
    voucherFieldset: false,
    documentFieldset: false,
    pricingFieldset: false,
    serviceFieldset: false,
    remarkFieldset: false,
    stayFieldset: false,
    qualityCheckFieldset: false,
};

const URL_SHORTCUT: { [key: string]: string[] } = {
    booking: ['h', 'ty', 'ai', 'at', 'aiexpert', 'atexpert', 'r', 's', 'mo', 'xt', 'is', 'rtr',
        'dcmissing', 'dcmax', 'dcmin',
        'dabcmin', 'dabcmax',
        'dlumissing', 'dlumin', 'dlumax',
        'dtimissing', 'dtimin', 'dtimax',
        'dimissing', 'dimin', 'dimax',
        'odmissing', 'odmax', 'odmin',
        'rtemissing', 'rtemin', 'rtemax',
        'dpmax', 'dpmin',
        'papmin', 'papmax',
        'ppmin', 'ppmax',
        'pvmin', 'pvmax'],
    flight: ['sss', 'sftext', 'sttext',
        'sbdmissing', 'sbdmin', 'sbdmax',
        'sadmissing', 'sadmin', 'sadmax',
        'soctext', 'sctext'],
    voucher: ['tma', 'tsftext', 'tsttext',
        'tsbdmissing', 'tsbdmin', 'tsbdmax',
        'ts', 'tsctext', 'tfn'],
    journey: ['ctext',
        'dbmissing', 'dbmax', 'dbmin',
        'demissing', 'demax', 'demin',
        'dwtrbmissing', 'dwtrbmax', 'dwtrbmin',
        'ftext', 'totext', 'ltext', 'tns',
        'ss'],

    traveller: [
        BookingSearchParametersShortName.person,
        BookingSearchParametersShortName.hasMembershipCard,
    ],
    document: ['dt'],
    pricing: ['pxr', 'pxs', 'pf'],
    service: ['svc', 'sxs', BookingSearchParametersShortName.hasSeatAssignment],
    afidiumServices: ['afs.0.sv'],
    remarkFieldset: [
        BookingSearchParametersShortName.remarkText,
        BookingSearchParametersShortName.remarkRemark,
        BookingSearchParametersShortName.remarkInvoicing,
        BookingSearchParametersShortName.remarkNote,
        BookingSearchParametersShortName.remarkOsi,
        BookingSearchParametersShortName.remarkVendorAlert,
    ],
    stay: [
        BookingSearchParametersShortName.placesToStay,
        BookingSearchParametersShortName.propertyName,
        BookingSearchParametersShortName.propertyReference,
        BookingSearchParametersShortName.stayBeginDate,
        BookingSearchParametersShortName.stayArrivalDate,
    ],
    qualityCheck: ['qc'],
    fareOptimisation: [
        BookingSearchParametersShortName.hasFareOptimisation,
        BookingSearchParametersShortName.actionForFareOptimisation,
        BookingSearchParametersShortName.savingMin,
        BookingSearchParametersShortName.impactOnFareType,
        BookingSearchParametersShortName.impactOnLuggage,
    ],

};

@Component({
    selector: 'booking-search-form',
    templateUrl: 'booking-search-form.component.html',
    styles: [`
        .spacing-bottom {
            padding-bottom: 15px;
            border-bottom: 1px solid #e6eaed;
        }
    `],
})
export class BookingSearchFormComponent implements OnInit, OnChanges, AfterViewInit {

    @Input()
    public searchParameters: BookingSearchParameters;

    @Input()
    public focusOnInit: boolean = false;

    @Output()
    public readonly onBookingSearchSubmit: EventEmitter<BookingSearchParameters> = new EventEmitter();

    @Output()
    public readonly reset: EventEmitter<null> = new EventEmitter();

    @ViewChild('cardForm')
    public cardForm: CardFormComponent;

    @ViewChild('textField')
    public textField: ChipsComponent;

    /** Enables to UpperCase all chips inputs. */
    public readonly chipsToUpper: boolean = true;

    public form: FormGroup;
    public fieldConfig: IFieldConfig;
    public formFields: IFieldConfig;
    public templateLabels: { [key: string]: string } = TEMPLATE_LABELS;
    public fieldsetState: { [key: string]: boolean } = FIELDSET_STATES;
    public fieldDescriptionLabels: { [key: string]: IDescriptionLabels } = FIELD_DESCRIPTION_LABELS;
    public sashaPermissions: typeof ESashaPermissions = ESashaPermissions;

    public cardOptions: IOptionsCardForm = {
        formGroup: null,
        labels: {
            error: CARD_OPTION_LABELS.error,
            submit: CARD_OPTION_LABELS.submit,
        },
    };

    constructor(
        private readonly fb: FormBuilder,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly configFieldBuilderService: ConfigFieldBuilderService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly searchUtilsService: SearchUtilsService,
    ) {
        this.form = formGroupConfig(this.fb);
    }

    public ngOnInit(): void {
        const {
            first,
            nbrecord,
            form,
            t,
            ...queries
        }: Params = this.activatedRoute.snapshot.queryParams as IBookingSearchParametersURL;

        if (Object.keys(queries).length) {
            this.fieldsetState = {
                bookingFieldset: URL_SHORTCUT.booking.some((query: string): boolean => Boolean(queries[query])),
                journeyFieldset: URL_SHORTCUT.journey.some((query: string): boolean => Boolean(queries[query])),
                travellerFieldset: URL_SHORTCUT.traveller.some((query: string): boolean => Boolean(queries[query])),
                flightFieldset: URL_SHORTCUT.flight.some((query: string): boolean => Boolean(queries[query])),
                voucherFieldset: URL_SHORTCUT.voucher.some((query: string): boolean => Boolean(queries[query])),
                documentFieldset: URL_SHORTCUT.document.some((query: string): boolean => Boolean(queries[query])),
                pricingFieldset: URL_SHORTCUT.pricing.some((query: string): boolean => Boolean(queries[query])),
                serviceFieldset: URL_SHORTCUT.service.some((query: string): boolean => Boolean(queries[query])),
                afidiumServiceFieldset: URL_SHORTCUT.afidiumServices.some((query: string): boolean => Boolean(queries[query])),
                remarkFieldset: URL_SHORTCUT.remarkFieldset.some((query: string): boolean => Boolean(queries[query])),
                stayFieldset: URL_SHORTCUT.stay.some((query: string): boolean => Boolean(queries[query])),
                qualityCheckFieldset: URL_SHORTCUT.qualityCheck.some((query: string): boolean => Boolean(queries[query])),
            };
        } else {
            this.fieldsetState = FIELDSET_STATES;
        }
        this.cardOptions.formGroup = this.form;
        this.formFields = this.configFieldBuilderService.buildFieldConfigArray(this.form, ALL_ARRAY_FIELDS);
        this.updateForm();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes.searchParameters.firstChange &&
            changes.searchParameters.currentValue !== changes.searchParameters.previousValue) {
            this.updateForm();
        }
    }

    public ngAfterViewInit(): void {
        if (this.focusOnInit) {
            this.focus();
            this.changeDetector.detectChanges();
        }
    }

    public onSubmit(): void {
        this.searchParameters = SearchUtils.populateSearchParametersFromForm<BookingSearchParameters>(
            this.form,
            this.searchParameters
        );
        // When launching a new search, we re-initialize the page number
        this.searchParameters.first = 1;

        this.onBookingSearchSubmit.emit(this.searchParameters);
    }

    public onReset(): void {
        this.cardForm.reset();
        this.form.reset();
        this.reset.emit();
    }

    public focus(): void {
        this.textField.focus();
    }

    private updateForm(): void {
        this.searchUtilsService.patchAllValues(
            this.form,
            this.searchParameters,
            {
                date: ALL_DATE_FIELDS,
                codeText: ALL_TEXT_FIELDS,
                array: ALL_ARRAY_FIELDS,
                formArray: ALL_FORM_ARRAY_FIELDS,
            }
        );
    }
}
